import React from 'react';
import { XMarkIcon, PrinterIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { formatDate, formatDateFull } from '../utils/dateUtils';

const AcceptanceDocument = ({ request, onClose }) => {
  const documentData = request.documentData || request;

  // Format date for Indonesian format (June, 02nd 2025)
  const formatDateIndonesian = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      const day = date.getDate();
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      
      // Add ordinal suffix to day
      const ordinal = (n) => {
        if (n > 3 && n < 21) return 'th';
        switch (n % 10) {
          case 1: return 'st';
          case 2: return 'nd';
          case 3: return 'rd';
          default: return 'th';
        }
      };
      
      return `${month}, ${day}${ordinal(day)} ${year}`;
    } catch (error) {
      return '-';
    }
  };

  // Format current datetime for Indonesian format
  const getCurrentDateTime = () => {
    const now = new Date();
    const days = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
    const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
                   'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
    
    const dayName = days[now.getDay()];
    const day = now.getDate();
    const month = months[now.getMonth()];
    const year = now.getFullYear();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    
    return `${dayName}, ${day} ${month} ${year} pukul ${hours}.${minutes}`;
  };

  const handlePrint = () => {
    const element = document.getElementById('acceptance-document');
    const printContent = element.innerHTML;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Acceptance of Service - ${documentData.order_id || 'Document'}</title>
          <meta charset="UTF-8">
          <style>
            body{font-family:Arial,sans-serif;margin:20px;line-height:1.4}.document-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:30px;padding-bottom:20px;border-bottom:3px solid #2c5aa0}.company-info{flex:1}.company-logo-section{display:flex;align-items:center;margin-bottom:10px}.company-logo{height:60px;margin-right:15px}.company-name{font-size:28px;font-weight:bold;color:#2c5aa0;margin-bottom:5px}.company-tagline{font-size:14px;color:#666;margin-bottom:2px}.head-office-info{flex:1;text-align:right;font-size:12px;color:#666;line-height:1.3}.document-title{text-align:center;font-size:24px;font-weight:bold;margin:30px 0 25px 0;color:#2c5aa0;text-transform:uppercase;letter-spacing:2px}.intro-text{background:#f8f9fa;padding:15px;margin-bottom:20px;border-left:4px solid #2c5aa0;font-style:italic;font-size:14px}.info-grid{display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:1px;margin-bottom:20px;border:1px solid #333}.info-cell{padding:12px;font-size:14px;border:1px solid #333;background:white}.info-header{background:#f8f9fa;font-weight:bold;color:#333}.highlight-green{color:#28a745;font-weight:bold}.address-row{display:grid;grid-template-columns:1fr 3fr;gap:1px;margin-bottom:1px}.address-cell{padding:12px;font-size:14px;border:1px solid #333}.address-header{background:#f8f9fa;font-weight:bold;color:#333}.circuit-info{display:grid;grid-template-columns:1fr 1fr 1fr;gap:1px;margin-bottom:20px;border:1px solid #333}.acceptance-info{display:grid;grid-template-columns:1fr 1fr;gap:1px;margin-bottom:30px;border:1px solid #333}.terms-section{margin:30px 0;padding:20px;background:#f9f9f9;border-left:4px solid #2c5aa0}.terms-title{font-weight:bold;color:#2c5aa0;margin-bottom:15px;font-size:16px}.terms-list{font-size:14px;line-height:1.6;color:#333}.terms-list p{margin-bottom:8px}.signature-section{margin-top:40px;display:flex;justify-content:space-between;gap:40px}.signature-block{flex:1;text-align:center}.signature-title{font-weight:bold;color:#2c5aa0;margin-bottom:20px;font-size:16px}.signature-info{margin-bottom:15px;font-size:14px}.signature-line{border-top:1px solid #333;margin:60px 20px 15px}.signature-date{font-weight:bold;margin-top:10px}.footer{margin-top:40px;text-align:center;font-size:12px;color:#666;border-top:1px solid #eee;padding-top:15px}.generation-info{margin-top:20px;text-align:center;font-size:11px;color:#999}
            @media print {
              body { margin: 0; padding: 20mm; }
              .no-print { display: none !important; }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handleDownload = () => {
    handlePrint(); // For now, use print functionality for download
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 no-print">
          <h3 className="text-lg font-medium text-gray-900">
            Acceptance of Service Document
          </h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrint}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PrinterIcon className="h-4 w-4 mr-2" />
              Print
            </button>
            <button
              onClick={handleDownload}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
              Download
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Document Content */}
        <div id="acceptance-document" className="p-8">
          <div className="document">
            <div className="document-header">
              <div className="company-info">
                <div className="company-logo-section">
                  <img 
                    src="https://rumahinternet.net/assets/images/logo-03052025.png" 
                    alt="Garuda Media Telematika Logo" 
                    className="company-logo"
                  />
                  <div>
                    <div className="company-name">GARUDA MEDIA TELEMATIKA</div>
                    <div className="company-tagline">Internet Service Provider</div>
                    <div className="company-tagline">Telecommunications &amp; Network Solutions</div>
                  </div>
                </div>
              </div>
              <div className="head-office-info">
                <strong>Head Office:</strong><br />
                Jl. Garuda N. 17A Blimbing<br />
                Gudo - Jombang, Jawa Timur<br />
                Tel: +62 321 - 6870123<br />
                Email: support@garuda.net.id<br />
                Web: garuda.net.id
              </div>
            </div>

            <div className="document-title">Acceptance of Service</div>

            <div className="intro-text">
              We would like to inform that Garuda Media Telematika Business service already established with the following details:
            </div>

            <div className="info-grid">
              <div className="info-cell info-header">Account ID</div>
              <div className="info-cell info-value">{documentData.account_id || 'N/A'}</div>
              <div className="info-cell info-header">Service Type</div>
              <div className="info-cell info-value">{documentData.service_type || 'N/A'}</div>
              
              <div className="info-cell info-header">Existing Bandwidth</div>
              <div className="info-cell info-value">{documentData.existing_bandwidth || documentData.current_bandwidth || 'N/A'}</div>
              <div className="info-cell info-header">New Bandwidth</div>
              <div className="info-cell info-value highlight-green">
                <strong>{documentData.new_bandwidth || documentData.requested_bandwidth || 'N/A'}</strong>
              </div>
              
              <div className="info-cell info-header">Order ID</div>
              <div className="info-cell info-value"><strong>{documentData.order_id || 'N/A'}</strong></div>
              <div className="info-cell info-header">Order Type</div>
              <div className="info-cell info-value">Upgrade</div>
            </div>

            <div className="address-row">
              <div className="address-cell address-header">Address Site A</div>
              <div className="address-cell address-value">{documentData.site_a_address || 'N/A'}</div>
            </div>

            <div className="address-row">
              <div className="address-cell address-header">Address Site B</div>
              <div className="address-cell address-value">{documentData.site_b_address || 'N/A'}</div>
            </div>

            <div className="circuit-info">
              <div className="info-cell info-header">Circuit ID</div>
              <div className="info-cell info-header">Date of Circuit Ready</div>
              <div className="info-cell info-header"></div>
            </div>

            <div className="circuit-info" style={{gridTemplateColumns: '1fr 1fr 1fr', marginTop: '1px'}}>
              <div className="info-cell info-value">{documentData.circuit_id || '-'}</div>
              <div className="info-cell info-value">{formatDateIndonesian(documentData.circuit_ready_date)}</div>
              <div className="info-cell info-value"></div>
            </div>

            <div className="acceptance-info">
              <div className="info-cell info-header">Date of Acceptance</div>
              <div className="info-cell info-header">Billing Start Date</div>
              <div className="info-cell info-value">{formatDateIndonesian(documentData.acceptance_date)} (as billing start date)</div>
              <div className="info-cell info-value">{formatDateIndonesian(documentData.acceptance_date)}</div>
            </div>

            <div className="terms-section">
              <div className="terms-title">Service Agreement Terms:</div>
              <div className="terms-list">
                <p>This document confirms the successful upgrade of internet bandwidth as requested by the customer.</p>
                <p>Billing for the new bandwidth will commence from the Date of Acceptance mentioned above.</p>
                <p>All terms and conditions of the original service agreement remain in effect.</p>
                <p>Technical support is available 24/7 through our customer service hotline.</p>
                <p>Any service issues should be reported immediately to ensure prompt resolution.</p>
              </div>
            </div>

            <div className="signature-section">
              <div className="signature-block">
                <div className="signature-title">Customer Acceptance</div>
                <div className="signature-info">{documentData.user?.company_name || documentData.company_name || 'Mitra Name'}</div>
                <div className="signature-line"></div>
                <div className="signature-info">{documentData.user?.contact_person || documentData.contact_person || 'Mitra Representative'}</div>
                <div className="signature-date">Date: {formatDateIndonesian(documentData.acceptance_date)}</div>
              </div>
              <div className="signature-block">
                <div className="signature-title">PT GARUDA MEDIA TELEMATIKA</div>
                <div className="signature-info">Sudarji</div>
                <div className="signature-line"></div>
                <div className="signature-info">Director</div>
                <div className="signature-date">Date: {formatDateIndonesian(documentData.acceptance_date)}</div>
              </div>
            </div>

            <div className="footer">
              This is a computer-generated document. For any inquiries, please contact our customer service at support@garuda.net.id or call +62 321 - 6870123
            </div>

            <div className="generation-info">
              Document generated on: {getCurrentDateTime()} | Order ID: {documentData.order_id || 'N/A'}
            </div>

            <div className="generation-info">
              <strong>ADDITIONAL INFORMATION</strong>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .document-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:30px;padding-bottom:20px;border-bottom:3px solid #2c5aa0}.company-info{flex:1}.company-logo-section{display:flex;align-items:center;margin-bottom:10px}.company-logo{height:60px;margin-right:15px}.company-name{font-size:28px;font-weight:bold;color:#2c5aa0;margin-bottom:5px}.company-tagline{font-size:14px;color:#666;margin-bottom:2px}.head-office-info{flex:1;text-align:right;font-size:12px;color:#666;line-height:1.3}.document-title{text-align:center;font-size:24px;font-weight:bold;margin:30px 0 25px 0;color:#2c5aa0;text-transform:uppercase;letter-spacing:2px}.intro-text{background:#f8f9fa;padding:15px;margin-bottom:20px;border-left:4px solid #2c5aa0;font-style:italic;font-size:14px}.info-grid{display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:1px;margin-bottom:20px;border:1px solid #333}.info-cell{padding:12px;font-size:14px;border:1px solid #333;background:white}.info-header{background:#f8f9fa;font-weight:bold;color:#333}.highlight-green{color:#28a745;font-weight:bold}.address-row{display:grid;grid-template-columns:1fr 3fr;gap:1px;margin-bottom:1px}.address-cell{padding:12px;font-size:14px;border:1px solid #333}.address-header{background:#f8f9fa;font-weight:bold;color:#333}.circuit-info{display:grid;grid-template-columns:1fr 1fr 1fr;gap:1px;margin-bottom:20px;border:1px solid #333}.acceptance-info{display:grid;grid-template-columns:1fr 1fr;gap:1px;margin-bottom:30px;border:1px solid #333}.terms-section{margin:30px 0;padding:20px;background:#f9f9f9;border-left:4px solid #2c5aa0}.terms-title{font-weight:bold;color:#2c5aa0;margin-bottom:15px;font-size:16px}.terms-list{font-size:14px;line-height:1.6;color:#333}.terms-list p{margin-bottom:8px}.signature-section{margin-top:40px;display:flex;justify-content:space-between;gap:40px}.signature-block{flex:1;text-align:center}.signature-title{font-weight:bold;color:#2c5aa0;margin-bottom:20px;font-size:16px}.signature-info{margin-bottom:15px;font-size:14px}.signature-line{border-top:1px solid #333;margin:60px 20px 15px}.signature-date{font-weight:bold;margin-top:10px}.footer{margin-top:40px;text-align:center;font-size:12px;color:#666;border-top:1px solid #eee;padding-top:15px}.generation-info{margin-top:20px;text-align:center;font-size:11px;color:#999}
      `}</style>
    </div>
  );
};

export default AcceptanceDocument;